"use client";

import React, { useState, useEffect } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Search, LayoutGrid, Code, ShieldCheck, Zap, Smartphone, Layers, Cpu, Globe } from "lucide-react";
const steps = [{
  id: "step-0",
  title: "01. Discovery",
  description: "We dive deep into your technical requirements and business goals, mapping out the architectural blueprint and defining the core technology stack for your digital product.",
  icon: Search,
  uiElements: ["Search Bar", "Requirement List", "Tech Stack Grid"]
}, {
  id: "step-1",
  title: "02. Design",
  description: "Our engineers and designers collaborate to create high-fidelity prototypes and scalable system architectures that prioritize both user experience and technical performance.",
  icon: LayoutGrid,
  uiElements: ["Wireframe Grid", "Color Palette", "Typography Scale"]
}, {
  id: "step-2",
  title: "03. Development",
  description: "Using agile sprints and modern frameworks, we forge your software with clean, maintainable code, ensuring every feature is built for speed and long-term scalability.",
  icon: Code,
  uiElements: ["Code Editor", "Terminal Output", "Git Commit Log"]
}, {
  id: "step-3",
  title: "04. QA & Testing",
  description: "Rigorous automated and manual testing protocols ensure your application is bug-free, secure, and ready for high-traffic environments before it ever reaches production.",
  icon: ShieldCheck,
  uiElements: ["Test Suite", "Bug Tracker", "Security Audit"]
}, {
  id: "step-4",
  title: "05. Scaling",
  description: "Post-launch, we monitor performance and implement cloud-native optimizations to ensure your product grows seamlessly alongside your expanding user base.",
  icon: Zap,
  uiElements: ["Traffic Monitor", "Server Load", "Global CDN"]
}];
export default function TheMatsyaJourney() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [activeStep, setActiveStep] = useState(steps[0].id);
  const [animating, setAnimating] = useState(false);
  useEffect(() => {
    setAnimating(true);
    const timer = setTimeout(() => setAnimating(false), 500);
    return () => clearTimeout(timer);
  }, [activeStep]);
  const currentStepData = steps.find(s => s.id === activeStep) || steps[0];
  return <section data-section-id="223897" id="the-agile-roadmap" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div ref={ref} className={`container mx-auto px-6 md:px-12 max-w-7xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="mb-16 md:mb-24 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tighter">
            The Agile Roadmap
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-mono">
            A systematic approach to engineering excellence, from initial concept 
            to global scale.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          {}
          <div className="order-2 lg:order-1 flex justify-center items-center">
            <div className="relative w-[280px] h-[580px] md:w-[320px] md:h-[640px] bg-card border-[8px] border-secondary rounded-lg shadow-2xl overflow-hidden">
              {}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-secondary rounded-lg z-50 flex items-center justify-center">
                <div className="w-12 h-1 bg-background/20 rounded-lg" />
              </div>

              {}
              <div className="relative h-full w-full bg-background p-6 pt-12 flex flex-col gap-6">
                {}
                <div className={`flex items-center justify-between transition-all duration-500 ${animating ? "opacity-0 -translate-y-4" : "opacity-100 translate-y-0"}`}>
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <currentStepData.icon className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex gap-2">
                    <div className="w-2 h-2 rounded-lg bg-muted" />
                    <div className="w-2 h-2 rounded-lg bg-muted" />
                  </div>
                </div>

                {}
                <div className={`w-full h-32 rounded-lg bg-muted/30 animate-pulse transition-all duration-500 delay-100 ${animating ? "opacity-0 scale-95" : "opacity-100 scale-100"}`}>
                  <div className="w-full h-full flex items-center justify-center">
                    <Layers className="w-12 h-12 text-muted-foreground/20" />
                  </div>
                </div>

                {}
                <div className="flex flex-col gap-4 flex-1">
                  {currentStepData.uiElements.map((item, i) => <div key={item} data-index={i} style={{
                  transitionDelay: `${(i + 2) * 100}ms`
                }} className={`p-4 rounded-lg border border-border bg-card/50 transition-all duration-500 ${animating ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0"}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-lg bg-primary" />
                        <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">{item}</span>
                      </div>
                    </div>)}
                </div>

                {}
                <div className={`mt-auto transition-all duration-500 delay-500 ${animating ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"}`}>
                  <div className="w-full h-12 rounded-lg bg-primary flex items-center justify-center shadow-lg">
                    <div className="w-24 h-2 bg-primary-foreground/30 rounded-lg" />
                  </div>
                </div>
              </div>

              {}
              <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-primary/10 blur-3xl rounded-lg pointer-events-none" />
            </div>
          </div>

          {}
          <div className="order-1 lg:order-2 relative py-4 pl-8 md:pl-12">
            <div className="absolute left-[11px] md:left-[23px] top-8 bottom-8 w-px border-l-2 border-dashed border-border" />

            <Accordion type="single" defaultValue={steps[0].id} onValueChange={val => {
            if (val) setActiveStep(val);
          }} className="w-full">
              {steps.map((step, i) => <div key={step.id} data-index={i} className="relative mb-8 last:mb-0">
                  <div className={`absolute -left-8 md:-left-12 top-4 w-6 h-6 rounded-lg border-4 border-background z-10 transition-all duration-300 ${activeStep === step.id ? "bg-primary shadow-md" : "bg-muted"}`} />
                  
                  <AccordionItem value={step.id} className="border-b-0 bg-card rounded-lg px-6 py-2 shadow-sm transition-all duration-300 data-[state=open]:shadow-lg data-[state=open]:bg-card/50 border border-border data-[state=open]:border-primary/50">
                    <AccordionTrigger className="text-xl md:text-2xl font-default font-bold text-foreground hover:no-underline py-4 tracking-tight">
                      {step.title}
                    </AccordionTrigger>
                    <AccordionContent className="text-base md:text-lg text-muted-foreground font-sans leading-relaxed pb-6 pt-2">
                      {step.description}
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}